import { content } from "../../config/content.js";
import {
  buildTeamAvatarRef,
  buildTeamAvatarSprite,
  experienceLabel,
} from "./TeamAvatar.js";

function renderMemberTile(member, index) {
  return `
    <button
      type="button"
      class="hero-logo-marquee__tile hero-logo-marquee__tile--member"
      data-index="${index}"
      aria-label="${member.name}, ${member.role}"
    >
      ${buildTeamAvatarRef(member, index, "sa", "square")}
    </button>
  `;
}

function buildMemberGroup(members, repeatCount) {
  return Array.from({ length: repeatCount }, () =>
    members.map((member, index) => renderMemberTile(member, index)).join("")
  ).join("");
}

export function createHeroMemberMarquee() {
  const members = content.team.members;
  const wrap = document.createElement("div");
  wrap.className = "hero-logo-marquee hero-logo-marquee--members";

  wrap.innerHTML = `
    ${buildTeamAvatarSprite(members, "sa")}
    <div class="hero-logo-marquee__glow"></div>
    <div class="hero-logo-marquee__viewport">
      <div class="hero-logo-marquee__track">
        <div class="hero-logo-marquee__group"></div>
        <div class="hero-logo-marquee__group" aria-hidden="true"></div>
      </div>
    </div>
    <div class="team__preview" aria-hidden="true">
      <div class="team__preview-card">
        <span class="team__preview-img"></span>
        <span class="team__preview-avatar"></span>
        <span class="team__preview-name"></span>
        <span class="team__preview-role"></span>
        <span class="team__preview-exp"></span>
        <span class="team__preview-tags"></span>
      </div>
    </div>
  `;

  bindInfiniteMemberMarquee(wrap, members);
  bindMemberPreview(wrap, members);
  return wrap;
}

function bindInfiniteMemberMarquee(wrap, members) {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const viewport = wrap.querySelector(".hero-logo-marquee__viewport");
  const track = wrap.querySelector(".hero-logo-marquee__track");
  const groups = [...wrap.querySelectorAll(".hero-logo-marquee__group")];

  const syncGroups = () => {
    if (reduced) {
      groups[0].innerHTML = buildMemberGroup(members, 1);
      groups[1].innerHTML = "";
      track.style.removeProperty("--marquee-duration");
      return;
    }

    const minWidth = viewport.offsetWidth || 0;
    let repeats = 2;

    groups[0].innerHTML = buildMemberGroup(members, repeats);
    groups[1].innerHTML = groups[0].innerHTML;

    while (groups[0].offsetWidth < minWidth && repeats < 24) {
      repeats += 1;
      groups[0].innerHTML = buildMemberGroup(members, repeats);
      groups[1].innerHTML = groups[0].innerHTML;
    }

    const distance = groups[0].offsetWidth || minWidth || 1;
    const pixelsPerSecond = 72;
    const duration = `${Math.max(distance / pixelsPerSecond, 12)}s`;

    track.style.setProperty("--marquee-duration", duration);

    track.style.webkitAnimation = "none";
    track.style.animation = "none";
    void track.offsetWidth;
    track.style.webkitAnimation = "";
    track.style.animation = "";
  };

  requestAnimationFrame(syncGroups);

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(syncGroups);
    observer.observe(viewport);
  } else {
    window.addEventListener("resize", syncGroups);
  }
}

function bindMemberPreview(wrap, members) {
  const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!fine || reduced) return;

  const viewport = wrap.querySelector(".hero-logo-marquee__viewport");
  const preview = wrap.querySelector(".team__preview");
  const previewImg = preview.querySelector(".team__preview-img");
  const avatar = preview.querySelector(".team__preview-avatar");
  const name = preview.querySelector(".team__preview-name");
  const role = preview.querySelector(".team__preview-role");
  const exp = preview.querySelector(".team__preview-exp");
  const tags = preview.querySelector(".team__preview-tags");

  let x = 0;
  let y = 0;
  let px = 0;
  let py = 0;
  let lastX = 0;
  let raf = null;

  const tick = () => {
    px += (x - px) * 0.18;
    py += (y - py) * 0.18;
    const tiltZ = Math.max(-8, Math.min(8, (x - lastX) * 0.35));
    lastX = x;
    preview.style.transform = `translate(${px}px, ${py}px) rotate(${tiltZ}deg)`;
    raf = requestAnimationFrame(tick);
  };

  viewport.addEventListener("mousemove", (e) => {
    x = e.clientX;
    y = e.clientY;
  });

  viewport.addEventListener("mouseenter", (e) => {
    x = px = e.clientX;
    y = py = e.clientY;
    wrap.classList.add("team--previewing");
    if (!raf) tick();
  });

  viewport.addEventListener("mouseleave", () => {
    wrap.classList.remove("team--previewing");
    if (raf) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  });

  viewport.addEventListener("mouseover", (e) => {
    const tile = e.target.closest(".hero-logo-marquee__tile--member");
    if (!tile) return;
    const member = members[Number(tile.dataset.index)];
    if (!member) return;

    previewImg.innerHTML = buildTeamAvatarRef(member, Number(tile.dataset.index), "sa");
    avatar.textContent = member.initials;
    name.textContent = member.name;
    role.textContent = member.role;
    exp.textContent = experienceLabel(member);
    tags.textContent = member.tags.join(" · ");

    const previewCard = preview.firstElementChild;
    previewCard.classList.remove("is-swapping");
    void previewCard.offsetWidth;
    previewCard.classList.add("is-swapping");
  });
}
